import { showConfetti, showCheckmark, showCelebrationText, shouldAnimate } from '../celebrations.js';
import { announce, announceProgress, announceSuccess } from '../accessibility.js';
import { emitSound, SoundEvents } from '../animationSystem.js';
import { hintManager } from '../hintManager.js';

/**
 * Base class for all mini-game implementations
 * Provides common interface and shared functionality
 */
export class MiniGameBase {
    // Registered event listeners for cleanup
    _boundListeners = [];
    // Guard against double-calling onComplete (race condition between celebration timeout and skip button)
    _completeCalled = false;

    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        this.data = deepDiveData;
        this.categoryName = categoryName;
        this.playerAnswers = [];
        this.completedItems = [];
        this.container = null;
        this.onComplete = onComplete;
        this.category = category;

        // Touch drag state
        this.touchDragState = {
            element: null,
            clone: null,
            startX: 0,
            startY: 0,
            offsetX: 0,
            offsetY: 0
        };
    }

    /**
     * Safely trigger the onComplete callback, guarding against double-calls.
     * This prevents race conditions between celebration timeouts and skip button.
     */
    triggerComplete() {
        if (this._completeCalled) {
            return; // Already called, ignore
        }
        this._completeCalled = true;
        if (this.onComplete) {
            this.onComplete();
        }
    }

    /**
     * Fisher-Yates shuffle algorithm
     * @param {Array} array - Array to shuffle (modified in place)
     * @returns {Array} The shuffled array
     */
    shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    /**
     * Register an event listener for automatic cleanup
     * @param {Element} element - DOM element
     * @param {string} event - Event type
     * @param {Function} handler - Event handler
     * @param {Object} options - addEventListener options
     */
    addListener(element, event, handler, options = {}) {
        element.addEventListener(event, handler, options);
        this._boundListeners.push({ element, event, handler, options });
    }

    /**
     * Find hint for a given text by looking up in category words
     * @param {string} text - The text to find a hint for
     * @returns {string|null} - The hint if found, null otherwise
     */
    findHintForText(text) {
        if (!this.category || !this.category.words) return null;
        const word = this.category.words.find(w =>
            w.text.toLowerCase() === text.toLowerCase()
        );
        return word ? word.hint : null;
    }

    /**
     * Attach hint handlers to an element if hint exists for the text
     * @param {HTMLElement} element - The element to attach handlers to
     * @param {string} text - The text to find a hint for
     */
    attachHintIfExists(element, text) {
        const hint = this.findHintForText(text);
        if (hint) {
            hintManager.attachHintHandlers(element, hint);
        }
    }

    /**
     * Render the mini-game UI
     * @param {HTMLElement} container - Container element to render into
     */
    render(container) {
        this.container = container;
        // Subclasses override this
        container.innerHTML = '<p>Base mini-game (override render method)</p>';
    }

    /**
     * Handle player input/interaction
     * @param {Object} selection - Player's selection/action
     * @returns {Object} Result of the action
     */
    handleInput(selection) {
        // Subclasses override this
        return { action: 'none' };
    }

    /**
     * Check if current answer is correct
     * @returns {boolean} Whether answer is correct
     */
    checkAnswer() {
        // Subclasses override this
        return false;
    }

    /**
     * Check if mini-game is complete
     * @returns {boolean} Whether all items are solved
     */
    isComplete() {
        // Subclasses override this
        return false;
    }

    /**
     * Play sound for item selection
     */
    playSelectSound() {
        emitSound(SoundEvents.SELECT);
    }

    /**
     * Play sound for correct match
     */
    playMatchSound() {
        emitSound(SoundEvents.MATCH_SUCCESS);
    }

    /**
     * Play sound for incorrect match
     */
    playErrorSound() {
        emitSound(SoundEvents.MATCH_FAIL);
    }

    /**
     * Set up touch drag support for an element
     * @param {HTMLElement} element - Element to make touch-draggable
     * @param {Function} onDragStart - Called when drag starts (element, index)
     * @param {Function} onDrop - Called when dropped (dragIndex, dropIndex)
     * @param {string} dropTargetSelector - CSS selector for valid drop targets
     * @param {number} index - Index of this draggable item
     */
    setupTouchDrag(element, onDragStart, onDrop, dropTargetSelector, index) {
        element.addEventListener('touchstart', (e) => {
            this.handleTouchStart(e, element, index, onDragStart);
        }, { passive: false });

        element.addEventListener('touchmove', (e) => {
            this.handleTouchMove(e, dropTargetSelector);
        }, { passive: false });

        element.addEventListener('touchend', (e) => {
            this.handleTouchEnd(e, dropTargetSelector, onDrop);
        }, { passive: false });
    }

    /**
     * Handle touch start - create dragging clone
     */
    handleTouchStart(e, element, index, onDragStart) {
        const touch = e.touches[0];
        const rect = element.getBoundingClientRect();

        this.touchDragState = {
            element: element,
            clone: null,
            dragIndex: index,
            startX: touch.clientX,
            startY: touch.clientY,
            offsetX: touch.clientX - rect.left,
            offsetY: touch.clientY - rect.top
        };

        // Small delay to distinguish from tap
        this.touchDragState.timeout = setTimeout(() => {
            // Disable hints while dragging
            hintManager.disableHints();

            // Create visual clone
            const clone = element.cloneNode(true);
            clone.classList.add('touch-dragging');
            clone.style.cssText = `
                position: fixed;
                left: ${rect.left}px;
                top: ${rect.top}px;
                width: ${rect.width}px;
                height: ${rect.height}px;
                z-index: 10000;
                pointer-events: none;
                opacity: 0.9;
                transform: scale(1.05);
                box-shadow: 0 8px 20px rgba(0,0,0,0.3);
            `;
            document.body.appendChild(clone);
            this.touchDragState.clone = clone;

            // Mark original as being dragged
            element.classList.add('dragging');

            if (onDragStart) {
                onDragStart(element, index);
            }
        }, 150);
    }

    /**
     * Handle touch move - move clone and highlight drop target
     */
    handleTouchMove(e, dropTargetSelector) {
        if (!this.touchDragState.clone) {
            // If moved before clone created, cancel the timeout
            if (this.touchDragState.timeout) {
                clearTimeout(this.touchDragState.timeout);
                this.touchDragState.timeout = null;
            }
            return;
        }

        e.preventDefault();
        const touch = e.touches[0];

        // Move clone
        const x = touch.clientX - this.touchDragState.offsetX;
        const y = touch.clientY - this.touchDragState.offsetY;
        this.touchDragState.clone.style.left = `${x}px`;
        this.touchDragState.clone.style.top = `${y}px`;

        // Find and highlight drop target
        this.touchDragState.clone.style.display = 'none';
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        this.touchDragState.clone.style.display = '';

        // Clear previous highlights
        if (this.container) {
            this.container.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over');
            });
        }

        // Highlight current target
        if (elementBelow) {
            const dropTarget = elementBelow.closest(dropTargetSelector);
            if (dropTarget && dropTarget !== this.touchDragState.element) {
                dropTarget.classList.add('drag-over');
            }
        }
    }

    /**
     * Handle touch end - perform drop
     */
    handleTouchEnd(e, dropTargetSelector, onDrop) {
        // Cancel timeout if drag didn't start
        if (this.touchDragState.timeout) {
            clearTimeout(this.touchDragState.timeout);
            this.touchDragState.timeout = null;
        }

        const clone = this.touchDragState.clone;
        const element = this.touchDragState.element;
        const dragIndex = this.touchDragState.dragIndex;

        if (!clone) {
            hintManager.enableHints(); // Re-enable hints even if no clone
            return;
        }

        // Find drop target
        const touch = e.changedTouches[0];
        clone.style.display = 'none';
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        clone.style.display = '';

        let dropIndex = null;
        if (elementBelow) {
            const dropTarget = elementBelow.closest(dropTargetSelector);
            if (dropTarget && dropTarget !== element) {
                dropIndex = parseInt(dropTarget.dataset.index);
                if (isNaN(dropIndex)) {
                    // For groupThem, use data-group instead
                    dropIndex = dropTarget.dataset.group || dropTarget.dataset.item;
                }
            }
        }

        // Clean up
        clone.remove();
        element.classList.remove('dragging');
        if (this.container) {
            this.container.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over');
            });
        }

        // Re-enable hints
        hintManager.enableHints();

        this.touchDragState = {
            element: null,
            clone: null,
            startX: 0,
            startY: 0,
            offsetX: 0,
            offsetY: 0
        };

        // Trigger drop callback
        if (dropIndex !== null && onDrop) {
            onDrop(dragIndex, dropIndex);
        }
    }

    /**
     * Show celebration animation
     */
    showCelebration() {
        // Play completion sound
        emitSound(SoundEvents.CATEGORY_COMPLETE);

        // Subclasses can override or use default
        if (shouldAnimate()) {
            showConfetti();
            if (this.container) {
                showCheckmark(this.container);
            }
        }

        // Show celebration text
        if (this.container) {
            showCelebrationText(this.container, '🎉 Complete!');
        }

        // Announce to screen readers
        announceSuccess('Mini-game complete!');

        // Auto-exit back to main board after celebration
        setTimeout(() => {
            this.triggerComplete();
        }, 2000); // 2 second delay to show celebration
    }

    /**
     * Announce progress for screen readers
     */
    announceProgress() {
        const total = this.data.items?.length || 0;
        const completed = this.completedItems.length;
        announceProgress(completed, total, 'pairs matched');
    }

    /**
     * Clean up all registered event listeners and DOM
     */
    destroy() {
        // Remove all registered listeners
        for (const { element, event, handler, options } of this._boundListeners) {
            try {
                element.removeEventListener(event, handler, options);
            } catch (e) {
                // Element may have been removed from DOM
            }
        }
        this._boundListeners = [];

        // Clear DOM
        if (this.container) {
            this.container.innerHTML = '';
            this.container = null;
        }
    }

    /**
     * Get progress string (e.g., "2/4 matched")
     * @returns {string} Progress indicator
     */
    getProgress() {
        const total = this.data.items?.length || 0;
        const completed = this.completedItems.length;
        return `${completed}/${total}`;
    }

    /**
     * Create a skip button for exiting the minigame early.
     * Always uses triggerComplete() to prevent race conditions.
     * @returns {HTMLButtonElement} The skip button element
     */
    createSkipButton() {
        const btn = document.createElement('button');
        btn.className = 'skip-button';
        btn.textContent = 'Skip game';
        btn.setAttribute('aria-label', 'Skip mini-game and return to main board');
        this.addListener(btn, 'click', () => this.triggerComplete());
        return btn;
    }
}
